import React, { useState } from "react";
import type { FC, PropsWithChildren } from "react";
import toast from "react-hot-toast";

import { useManualTimer } from "../../hooks/patients";

import { Modal, Button, InputsSection } from "../../components/Modal";
import ModalHeading from "../../components/Modal/ModalHeading";
import InputField from "../../components/Modal/InputField";

import { IPatientRes } from "../../hooks/patients/index.type";

import classes from "../../styles/ManualTimer.module.css";

export type ManualTimerEntryModalState = PropsWithChildren<{
  status: boolean;
}>;

type ManualTimerEntryModalProps = PropsWithChildren<{
  ManualTimerModal: ManualTimerEntryModalState;
  onCloseModal: () => void;
  patientId: string;
  clinicId: string;
  onSuccess?: () => void;
  patientData?: IPatientRes;
  setPatientData?: React.Dispatch<
    React.SetStateAction<IPatientRes | undefined>
  >;
  patientName?: string;
}>;

const ManualTimerEntryModal: FC<ManualTimerEntryModalProps> = (props) => {
  const [minutes, setMinutes] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const { StartManualTimer, StartManualTimerLoading } = useManualTimer({
    patientId: props.patientId,
    clinicId: props.clinicId,
  });

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setMinutes(value);
  };

  const handleSave = async () => {
    if (!minutes || parseInt(minutes) <= 0) {
      toast.error("Please enter valid minutes");
      return;
    }

    setIsSaving(true);
    try {
      const response = await StartManualTimer({
        billingMinutes: parseInt(minutes),
      });

      if (response?.status === "success" && response.data) {
        // Immediate UI update using response data (most optimized)
        if (props.setPatientData && response.data) {
          const updatedPatient = response.data;
          props.setPatientData((prev) => {
            if (!prev) return prev;
            const newPatientData = prev.data.data.map((p) => {
              if (p._id === updatedPatient._id) {
                return { ...p, timeSpent: updatedPatient.timeSpent };
              }
              return p;
            });
            return {
              ...prev,
              data: {
                ...prev.data,
                data: newPatientData,
              },
            };
          });
        }

        if (props.onSuccess) {
          props.onSuccess();
        }

        props.onCloseModal();
        setMinutes("");
      } else {
        toast.error("Failed to add manual time entry");
      }
    } catch (error) {
      console.error("Error in handleSave:", error);
      toast.error("Failed to add manual time entry");
    } finally {
      setIsSaving(false);
    }
  };

  const isDisabled = StartManualTimerLoading || isSaving;

  return (
    <Modal
      show={props.ManualTimerModal.status}
      modalClassName={classes.manual_timer_modal}
    >
      <ModalHeading
        heading="Manual Entry (Minutes)"
        customClass={classes.manual_timer_heading}
      />

      {props.patientName && (
        <div
          className={classes.patient_name_display}
          style={{ marginBottom: "0.8rem" }}
        >
          Patient Name: <span>{props.patientName}</span>
        </div>
      )}

      <div className={classes.full_width}>
        <InputsSection customClass={classes.manual_timer_input_section}>
          <InputField
            placeholder="Enter minutes"
            value={minutes}
            onChange={handleMinutesChange}
            customClass={classes.manual_timer_input}
          />
        </InputsSection>
      </div>

      <div className={classes.manual_timer_button_container}>
        <Button
          label="Cancel"
          span={false}
          customClass={`${classes.manual_timer_button} ${classes.manual_timer_cancel_button}`}
          onClick={props.onCloseModal}
        />
        <Button
          label={isDisabled ? "Saving..." : "Save"}
          span={false}
          disabled={isDisabled}
          customClass={`${classes.manual_timer_button} ${
            isDisabled ? classes.save_button_disabled : classes.save_button_blue
          }`}
          onClick={handleSave}
        />
      </div>
    </Modal>
  );
};

export default ManualTimerEntryModal;
